/**
 * Default work-item status categories — display and filter order.
 * Query param values: NEW, ESTIMATIONS, IN_REVIEW, IN_PROGRESS, ON_HOLD, DONE.
 */
export const DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER = [
  "NEW",
  "ESTIMATIONS",
  "IN_REVIEW",
  "IN_PROGRESS",
  "ON_HOLD",
  "DONE",
];

/** Kanban boards (global + project) — In Progress before In Review. */
export const KANBAN_BOARD_STATUS_CATEGORY_ORDER = [
  "NEW",
  "ESTIMATIONS",
  "IN_PROGRESS",
  "IN_REVIEW",
  "ON_HOLD",
  "DONE",
];

/** Project Kanban board — five category columns (no On Hold). */
export const PROJECT_KANBAN_STATUS_CATEGORY_ORDER = [
  "NEW",
  "ESTIMATIONS",
  "IN_PROGRESS",
  "IN_REVIEW",
  "DONE",
];

/** Status dropdowns — all six categories (same order as Kanban). */
export const PROJECT_STATUS_DROPDOWN_CATEGORY_ORDER = KANBAN_BOARD_STATUS_CATEGORY_ORDER;

/** @typedef {{ label: string, kanbanClass: string, badge: { backgroundColor: string, color: string, borderColor: string }, workflow: { bg: string, text: string } }} StatusCategoryThemeEntry */

/** @type {Record<string, StatusCategoryThemeEntry>} */
export const WORK_ITEM_STATUS_CATEGORY_THEME = {
  NEW: {
    label: "New",
    kanbanClass: "border-indigo-200 bg-indigo-50 text-indigo-800",
    badge: {
      backgroundColor: "#EEF2FF",
      color: "#4338CA",
      borderColor: "#C7D2FE",
    },
    workflow: { bg: "#EEF2FF", text: "#4338CA" },
  },
  ESTIMATIONS: {
    label: "Estimated",
    kanbanClass: "border-amber-200 bg-amber-50 text-amber-800",
    badge: {
      backgroundColor: "#FFFBEB",
      color: "#B45309",
      borderColor: "#FCD34D",
    },
    workflow: { bg: "#FFFBEB", text: "#B45309" },
  },
  IN_PROGRESS: {
    label: "In Progress",
    kanbanClass: "border-blue-200 bg-blue-50 text-blue-800",
    badge: {
      backgroundColor: "#EFF6FF",
      color: "#1D4ED8",
      borderColor: "#BFDBFE",
    },
    workflow: { bg: "#EFF6FF", text: "#1D4ED8" },
  },
  IN_REVIEW: {
    label: "In Review",
    kanbanClass: "border-sky-200 bg-sky-50 text-sky-800",
    badge: {
      backgroundColor: "#E0F2FE",
      color: "#0369A1",
      borderColor: "#7DD3FC",
    },
    workflow: { bg: "#E0F2FE", text: "#0369A1" },
  },
  ON_HOLD: {
    label: "On Hold",
    kanbanClass: "border-orange-200 bg-orange-50 text-orange-900",
    badge: {
      backgroundColor: "#FFF7ED",
      color: "#C2410C",
      borderColor: "#FDBA74",
    },
    workflow: { bg: "#FFF7ED", text: "#C2410C" },
  },
  CLOSED: {
    label: "Done",
    kanbanClass: "border-emerald-200 bg-emerald-50 text-emerald-800",
    badge: {
      backgroundColor: "#D1FAE5",
      color: "#065F46",
      borderColor: "#6EE7B7",
    },
    workflow: { bg: "#D1FAE5", text: "#065F46" },
  },
  /** Same theme as CLOSED — used in filters and `statusCategories` query. */
  DONE: {
    label: "Done",
    kanbanClass: "border-emerald-200 bg-emerald-50 text-emerald-800",
    badge: {
      backgroundColor: "#D1FAE5",
      color: "#065F46",
      borderColor: "#6EE7B7",
    },
    workflow: { bg: "#D1FAE5", text: "#065F46" },
  },
};

/** Legacy / API alias → canonical theme key */
export const WORK_ITEM_STATUS_CATEGORY_ALIASES = {
  TODO: "NEW",
  TO_DO: "NEW",
  OPEN: "NEW",
  ESTIMATION: "ESTIMATIONS",
  ESTIMATED: "ESTIMATIONS",
  REVIEW: "IN_REVIEW",
  INPROGRESS: "IN_PROGRESS",
  "IN-PROGRESS": "IN_PROGRESS",
  ONHOLD: "ON_HOLD",
  HOLD: "ON_HOLD",
  COMPLETED: "DONE",
  CLOSED: "DONE",
  BLOCKED: "ON_HOLD",
  TO_TESTING: "IN_REVIEW",
  TESTING: "IN_REVIEW",
};

/**
 * @param {string} rawCategory
 * @returns {string}
 */
export function normalizeWorkItemStatusCategoryKey(rawCategory) {
  const cat = String(rawCategory ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (!cat) return "";
  if (WORK_ITEM_STATUS_CATEGORY_THEME[cat]) return cat;
  return WORK_ITEM_STATUS_CATEGORY_ALIASES[cat] ?? cat;
}

/**
 * @param {string} categoryKey
 * @returns {string}
 */
export function getWorkItemStatusCategoryLabel(categoryKey) {
  const key = normalizeWorkItemStatusCategoryKey(categoryKey);
  return WORK_ITEM_STATUS_CATEGORY_THEME[key]?.label ?? "Status";
}

/**
 * @param {string} categoryKey
 * @returns {StatusCategoryThemeEntry["badge"]}
 */
export function getWorkItemStatusCategoryBadgeStyle(categoryKey) {
  const key = normalizeWorkItemStatusCategoryKey(categoryKey);
  return (
    WORK_ITEM_STATUS_CATEGORY_THEME[key]?.badge ?? {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#E5E7EB",
    }
  );
}

/**
 * @param {string} categoryKey
 * @returns {string}
 */
export function getWorkItemStatusCategoryKanbanClass(categoryKey) {
  const key = normalizeWorkItemStatusCategoryKey(categoryKey);
  return (
    WORK_ITEM_STATUS_CATEGORY_THEME[key]?.kanbanClass ?? "border-gray-200 bg-gray-50 text-gray-800"
  );
}

/**
 * @param {string} categoryKey
 * @returns {{ bg: string, text: string }}
 */
export function getWorkItemStatusCategoryWorkflowColors(categoryKey) {
  const key = normalizeWorkItemStatusCategoryKey(categoryKey);
  return (
    WORK_ITEM_STATUS_CATEGORY_THEME[key]?.workflow ?? {
      bg: "#F3F4F6",
      text: "#374151",
    }
  );
}

/** Map workflow/API category to sort bucket (CLOSED → DONE). */
export function workItemStatusCategorySortKey(rawCategory) {
  const key = normalizeWorkItemStatusCategoryKey(rawCategory);
  if (key === "CLOSED") return "DONE";
  return key;
}

/** Sort index for {@link DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER}. */
export function getWorkItemStatusCategorySortIndex(rawCategory) {
  const sortKey = workItemStatusCategorySortKey(rawCategory);
  const idx = DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER.indexOf(sortKey);
  return idx === -1 ? DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER.length : idx;
}

/** Sort index for Kanban columns ({@link KANBAN_BOARD_STATUS_CATEGORY_ORDER}). */
export function getKanbanStatusCategorySortIndex(rawCategory) {
  const sortKey = workItemStatusCategorySortKey(rawCategory);
  const idx = KANBAN_BOARD_STATUS_CATEGORY_ORDER.indexOf(sortKey);
  return idx === -1 ? KANBAN_BOARD_STATUS_CATEGORY_ORDER.length : idx;
}

/**
 * Values for GET `statusCategories` — uses NEW … DONE tokens (not CLOSED).
 * @param {string|string[]} raw
 * @returns {string|string[]}
 */
export function normalizeWorkItemStatusCategoriesForApi(raw) {
  const mapOne = (v) => {
    const key = workItemStatusCategorySortKey(v);
    if (!key) return "";
    if (DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER.includes(key)) return key;
    return String(v ?? "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");
  };
  if (Array.isArray(raw)) return raw.map(mapOne).filter(Boolean);
  return mapOne(raw);
}

/** Filter dropdown options for the six default categories (values match query params). */
export const DEFAULT_WORK_ITEM_STATUS_CATEGORY_FILTER_OPTIONS =
  PROJECT_STATUS_DROPDOWN_CATEGORY_ORDER.map((value) => ({
    value,
    label: WORK_ITEM_STATUS_CATEGORY_THEME[value].label,
  }));

function pickDefaultWorkflowStatusIdForCategory(statuses, category) {
  const list = Array.isArray(statuses) ? statuses : [];
  const want = workItemStatusCategorySortKey(category);
  const matches = list.filter((s) => workItemStatusCategorySortKey(s?.category) === want);
  matches.sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
  const id = matches[0]?.id;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

/**
 * Project status dropdown — one option per category (all six: New … On Hold … Done).
 * `value` is the default workflow status id for that category (API PATCH) when configured.
 * @param {Array<{ id: string|number, name?: string, category?: string, sequenceOrder?: number }>} statuses
 */
export function buildProjectStatusOptionsByCategory(statuses) {
  const list = Array.isArray(statuses) ? statuses : [];
  return PROJECT_STATUS_DROPDOWN_CATEGORY_ORDER.map((category) => ({
    label: getWorkItemStatusCategoryLabel(category),
    value: pickDefaultWorkflowStatusIdForCategory(list, category) ?? "",
    category,
  }));
}

/**
 * Maps a work item to the matching project status option value (by category).
 */
export function resolveProjectStatusOptionValue(issue, statuses, options) {
  if (!issue || !Array.isArray(options) || !options.length) return "";

  const list = Array.isArray(statuses) ? statuses : [];
  let catKey = workItemStatusCategorySortKey(issue.statusCategory);

  if (!catKey && issue.statusId != null && list.length) {
    const row = list.find((s) => String(s.id) === String(issue.statusId));
    catKey = workItemStatusCategorySortKey(row?.category);
  }

  if (catKey) {
    const opt = options.find((o) => workItemStatusCategorySortKey(o.category) === catKey);
    if (opt?.value) return String(opt.value);
  }

  if (issue.statusId != null) {
    const exact = options.find((o) => String(o.value) === String(issue.statusId));
    if (exact) return String(exact.value);
  }

  if (issue.status) {
    const byLabel = options.find((o) => String(o.label) === String(issue.status));
    if (byLabel?.value) return String(byLabel.value);
  }

  return "";
}

/** Normalize GET /projects/:id/statuses (or Redux slice entry) to a workflow status array. */
export function getProjectWorkflowStatusesList(statusesPayload) {
  if (statusesPayload == null) return [];
  if (Array.isArray(statusesPayload)) return statusesPayload;
  if (Array.isArray(statusesPayload.data)) return statusesPayload.data;
  if (statusesPayload.data != null && Array.isArray(statusesPayload.data.data)) {
    return statusesPayload.data.data;
  }
  return [];
}

/**
 * When moving a work item to another project, pick a valid `statusId` on the target workflow
 * (same status category when possible — matches project status dropdown options).
 */
export function remapWorkItemStatusForTargetProject(issueLike, statusesPayload) {
  const statuses = getProjectWorkflowStatusesList(statusesPayload);
  if (!statuses.length) {
    return {
      statusId: issueLike?.statusId != null ? String(issueLike.statusId) : null,
      status: issueLike?.status != null ? String(issueLike.status) : "",
      statusCategory: issueLike?.statusCategory ?? null,
    };
  }

  const options = buildProjectStatusOptionsByCategory(statuses);
  let statusId = resolveProjectStatusOptionValue(issueLike, statuses, options);
  if (!statusId) {
    statusId = pickDefaultWorkflowStatusIdForCategory(statuses, "NEW");
  }
  if (!statusId) {
    const sorted = [...statuses].sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
    statusId = sorted[0]?.id != null ? String(sorted[0].id) : null;
  }

  const opt = options.find((o) => statusId != null && String(o.value) === String(statusId));
  return {
    statusId: statusId || null,
    status: opt?.label ?? (issueLike?.status != null ? String(issueLike.status) : ""),
    statusCategory: opt?.category ?? issueLike?.statusCategory ?? null,
  };
}
